import React, { useState, useEffect, useRef } from "react"
import PubSub from "pubsub-js"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import { EnvelopeIcon, ArrowLeftIcon } from "@heroicons/react/24/outline"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import CMSData from "aldoo-ra/CMS/cms-data"
import AldooStoreAPI from "aldoo-ra/Store/api"

const EmailInputDialog = ({ store, initialEmail = "", isActive }) => {
  const texts = TextBundle({ id: "aldoo-store" })
  const [email, setEmail] = useState(initialEmail)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [showMagicLink, setShowMagicLink] = useState(false)
  const [verificationCredentials, setVerificationCredentials] = useState(null)
  const [googleLoading, setGoogleLoading] = useState(false)
  const googleSigninRef = useRef(false)

  const [auth] = PlatformAuth.useAuth()
  const { state: providerState, signin: signinWithProvider } = PlatformAuth.useSigninWithProvider()

  const { data: cmsSigninData } = CMSData({ contentType: "SigninDialog" })
  const googleProvider = cmsSigninData?.[0]?.providers?.find((p) => p.id === "google")

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleSubmit = async () => {
    if (!email.trim()) {
      setError(texts?.emailRequired || "Email is required")
      return false
    }

    if (!validateEmail(email)) {
      setError(texts?.invalidEmail || "Please enter a valid email address")
      return false
    }

    setLoading(true)
    setError("")

    try {
      const result = await store.handleEmailVerification(email)

      if (result?.requiresVerification) {
        setVerificationCredentials(result.credentials)
        setShowMagicLink(true)
        setLoading(false)

        const verified = await store.waitForVerification(result.credentials)

        if (verified) {
          setShowMagicLink(false)
          PubSub.publish("PURCHASE_FLOW_UPDATE_BUTTON", {
            stepId: 'email',
            hideButtons: false
          })
          PubSub.publish("PURCHASE_FLOW_NEXT", { stepData: { email } })
          return true
        } else {
          setShowMagicLink(false)
          setError(texts?.verificationFailed || "Verification failed or timed out")
          setLoading(false)
          return false
        }
      } else {
        setLoading(false)
        PubSub.publish("PURCHASE_FLOW_NEXT", { stepData: { email } })
        return true
      }
    } catch (err) {
      setError(err.message || texts?.genericError || "An error occurred")
      setLoading(false)
      return false
    }
  }

  const handleGoogleClick = () => {
    setGoogleLoading(true)
    googleSigninRef.current = true
    signinWithProvider({ id: "google" })
  }

  useEffect(() => {
    if (!googleSigninRef.current || providerState !== "success" || !auth?.email) return
    googleSigninRef.current = false
    const userEmail = auth.email
    setGoogleLoading(false)

    AldooStoreAPI.loadBillingDetails()
      .then((invoiceData) => store.setPreloadedInvoiceData(invoiceData))
      .catch(() => store.setPreloadedInvoiceData(null))
      .finally(() => PubSub.publish("PURCHASE_FLOW_NEXT", { stepData: { email: userEmail } }))
  }, [providerState, auth])

  const handleMagicLinkBack = () => {
    setShowMagicLink(false)
    setLoading(false)
    setVerificationCredentials(null)
  }

  useEffect(() => {
    if (!isActive) return

    if (showMagicLink) {
      PubSub.publish("PURCHASE_FLOW_UPDATE_BUTTON", {
        stepId: 'email',
        canContinue: false,
        loading: false,
        hideButtons: true,
        onContinue: handleSubmit,
        onBack: () => PubSub.publish("PURCHASE_FLOW_BACK")
      })
    } else {
      PubSub.publish("PURCHASE_FLOW_UPDATE_BUTTON", {
        stepId: 'email',
        canContinue: !!email.trim() && !loading,
        loading: loading,
        hideButtons: false,
        onContinue: handleSubmit,
        onBack: () => PubSub.publish("PURCHASE_FLOW_BACK")
      })
    }
  }, [email, loading, showMagicLink, isActive])

  if (showMagicLink && verificationCredentials) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="space-y-6 w-full max-w-md px-6 py-8 flex flex-col justify-center">
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="bg-primary_lt/30 rounded-full p-4">
              <EnvelopeIcon className="h-16 w-16 text-primary" />
            </div>

            <Typography variant="h3" className="text-text">
              {texts?.checkYourEmail || "Check Your Email"}
            </Typography>

            <div className="w-full border-t border-bordered my-4" />

            <Typography variant="body1_medium" className="text-text">
              {verificationCredentials.email}
            </Typography>

            <Typography variant="txt" className="text-text_secondary">
              {texts?.magicLinkSent || "Please verify your email to continue"}
            </Typography>

            <Button
              variant="text_btn_ghost"
              onClick={handleMagicLinkBack}
              className="flex items-center gap-2 mt-6"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span>{texts?.madeMistake || "Made a mistake?"}</span>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full flex items-center justify-center">
      <div className="space-y-6 w-full max-w-md px-6 py-8 flex flex-col justify-center">
        <div className="flex flex-col items-center text-center space-y-4">
          <div className="bg-primary_lt/30 rounded-full p-4">
            <EnvelopeIcon className="h-8 w-8 text-primary" />
          </div>

          <Typography variant="h3" className="text-text">
            {texts?.enterYourEmail || "Enter Your Email"}
          </Typography>

          <Typography variant="body1_dim" className="text-text_secondary">
            {texts?.emailDialogDescription || "We'll use this to send your receipt and account details"}
          </Typography>
        </div>

        <div className="space-y-2 max-w-md mx-auto w-full">
          <Input
            type="email"
            value={email}
            onChange={(e) => {
              const newEmail = e.target.value
              setEmail(newEmail)
              if (error) setError("")
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !loading) handleSubmit(e)
            }}
            placeholder={texts?.emailPlaceholder || "Please enter your email"}
            autoFocus
            disabled={loading}
            className={`text-center ${error ? "border-error" : ""}`}
          />

          {error && (
            <Typography variant="txt1" className="text-error mt-2 text-center">
              {error}
            </Typography>
          )}
        </div>

        {googleProvider && (
          <>
            <div className="relative flex items-center">
              <div className="flex-1 border-t border-bordered" />
              <span className="px-3 text-xs text-text_secondary">{texts?.orContinueWith || "or"}</span>
              <div className="flex-1 border-t border-bordered" />
            </div>
            <Button
              variant="sign_in_option"
              className="bg-[#4285F4] hover:bg-[#3367d6] w-full"
              onClick={handleGoogleClick}
              disabled={googleLoading || loading}
            >
              <img src={googleProvider.logo} alt="Google" className="w-6 h-6" />
              <span>{googleProvider.cta || texts?.continueWithGoogle || "Continue with Google"}</span>
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

export default EmailInputDialog
